import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import {
  getStoredUtmParams,
  mergeUtmParams,
  pickUtmParams,
  TUtmParams,
} from "@shared/tracking";

/**
 * The UTM parameters to append to our links, taken from the visit the user is on and
 * falling back to the hard-coded `defaults` per missing key.
 *
 * Neither source is available while the html is built: pages like the call page and the
 * call grid are statically rendered, and session storage only exists on the client. React
 * doesn't patch mismatched attributes such as `href` while hydrating, so both are read
 * *after* the first render instead — `router.query`, which next populates right after
 * hydration, and the stored params, read in an effect. Either update re-renders the links
 * with the real parameters, which is what corrects them.
 *
 * The stored params are re-read on every navigation rather than on mount only. `_app` writes
 * them in a mount effect of its own, and react runs a child's effects before its parent's, so
 * on the first page of a visit this hook necessarily reads storage before `_app` has filled
 * it. That first read is harmless (the url the visit arrived on still carries the params, and
 * a later page load reads what the earlier one stored), but a client-side navigation to a url
 * without them — a call page tab, or closing the QuickInfo drawer — would otherwise be left
 * with nothing to fall back to. Note react's strict mode hides this in development by
 * invoking effects twice.
 *
 * The result is memoised on the resolved values rather than on `router.query`, whose identity
 * we don't control, so consumers can safely keep it in a dependency array.
 */
export default function useUtmParams(
  defaults?: Partial<TUtmParams>,
): TUtmParams {
  const { query, asPath } = useRouter();
  const [storedParams, setStoredParams] = useState<Partial<TUtmParams>>({});

  useEffect(() => {
    const stored = getStoredUtmParams();
    // Same values means the same object, so react bails out instead of re-rendering every
    // consumer — which is the common case: nothing stored, and nothing new on a navigation
    setStoredParams((current) =>
      JSON.stringify(current) === JSON.stringify(stored) ? current : stored,
    );
  }, [asPath]);

  // Serialised, because an inline `{ utm_medium: "call_page" }` literal and router.query are
  // fresh objects on every render: keying the memo on the values is what keeps the result
  // stable. The url wins over what an earlier page of the visit stored.
  const visitParams = { ...storedParams, ...pickUtmParams(query) };
  const resolvedKey = JSON.stringify([defaults ?? {}, visitParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the resolved values
  return useMemo(() => mergeUtmParams(defaults, visitParams), [resolvedKey]);
}
